import React, { useState, useEffect } from 'react';
import LoadingIndicator from './../LoadingIndicator';
import { API_KEY, BASE_URL } from "../config";

function SpecialConfigurationDetails({ signalRequestId, refreshTrigger }) {
  const [config, setConfig] = useState(null); 
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [otherSelected, setOtherSelected] = useState({});

  const unifiedButtonStyle = {
    padding: '6px 12px',
    fontSize: '13px',
    margin: '5px 8px 5px 0',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'rgba(57, 65, 65, 0.85)', // equivalent of #394141da
    color: '#fff',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  useEffect(() => {
    if (isSaving || isEditing) {
      return;
    }
    fetch(`${BASE_URL}/api/bot/ui/post/getSpecialConfiguration?signalRequestId=${signalRequestId}`,
                    {
                      headers: {
                        "X-SIGNALFLY-KEY": API_KEY
                      }
                    })
      .then(response => response.json())
      .then(data => {
        setConfig(data[0]); 
        setLoading(false);
      })
      .catch(error => {
        setError(error.message);
        setLoading(false);
      });
  }, [signalRequestId, refreshTrigger, isSaving, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };



  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    const configToSave = { ...config };

    fetch(`${BASE_URL}/api/bot/ui/post/saveSpecialConfiguration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json',
                  "X-SIGNALFLY-KEY": API_KEY },
      body: JSON.stringify(configToSave),
    })
    .then(response => response.json())
    .then(() => {
      setIsEditing(false);
      setIsSaving(false);
      setLoading(false);
    })
    .catch(error => {
      setError(error.message);
      setIsSaving(false);
      setLoading(false);
    });
  };

  const handleDefaultConfigurationClick = () => {
    if (!defaultConfig) {
      fetch(`${BASE_URL}/api/bot/ui/post/getSystemDefaultConfiguration`,
                      {
                        headers: {
                          "X-SIGNALFLY-KEY": API_KEY
                        }
                      })
        .then(response => response.json())
        .then(defaultData => setDefaultConfig(defaultData))
        .catch(error => setError(error.message));
    } else {
      setDefaultConfig(null);
    }
  };

  const resetToDefaultConfig = () => {
    setLoading(true);
    fetch(`${BASE_URL}/api/bot/ui/post/deleteSpecialConfiguration?id=${config.id}`, {
      method: 'DELETE',
      headers: {
        "X-SIGNALFLY-KEY": API_KEY
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then(() => {
        setConfig({ ...config, /* default values */ });
        setLoading(false);
        setIsEditing(false);
      })
      .catch(error => {
        setError(error.message);
        setLoading(false);
      });
  };


  const dropdownFields = {
    hedgeManagementEnabled: ['','true', 'false'],
    hedgeSizeMultiplierEnabled: ['','true', 'false'],
    hedgeSizeMultiplier: [1,2,3,4,5],
    repelTrade: ['','true', 'false'],
    signalMovingPlanEnabled: ['','true', 'false'],
    signalMovingPlanRangeRate: ['','1','1.5','2','3','0.1','0.2','0.3','0.5','Other'],
    signalMovingPlanTriggerPriceDistance: ['','25','50','75','100','Other'],
    hedgeMovingPlanEnabled: ['','true', 'false'],
    hedgeMovingPlanRangeRate: ['','1','1.5','2','3','0.1','0.2','0.3','0.5','Other'],
    hedgeMovingPlanTriggerPriceDistance: ['','25','50','75','100','Other'],
    flexiHedgeSizeFlag: ['','true', 'false'],
    flexiHedgeSizeInPercent: ['','10','25','50','75','100','Other'],
    lossTolerantPercent: ['','30','40','50','60','70','100','Other'],
    signalOffsetDistanceForMinProfit: ['','20','30','40','50','60','70','80','90','100','Other'],
    signalStopLossDistanceForMinProfit: ['','20','30','40','50','60','70','80','90','100','Other'],
    signal2ndLevelOffsetDistance: ['','20','30','40','50','60','70','80','90','100','Other'],
    signal2ndLevelStopLossDistance: ['','20','30','40','50','60','70','80','90','100','Other'],
    hedgeOffsetDistanceForMinProfit: ['','20','30','40','50','60','70','80','90','100','Other'],
    hedgeStopLossDistanceForMinProfit: ['','20','30','40','50','60','70','80','90','100','Other'],
    priceDistanceToOpenNextHedge: ['','0.25','0.3','0.4','0.5','0.6','0.7','0.8','0.9','1','Other'],
    priceDistanceToCloseHedge: ['','0.125','0.25','0.3','0.4','0.5','0.6','0.7','0.8','0.9','1','Other'],
    maxHedgeCountAllowed: [5,4,3,2,1],
    // Add other fields as necessary
  };


  const handleChange = (e, key) => {
    const updatedValue = e.target.value;
    setConfig(prevConfig => ({ ...prevConfig, [key]: updatedValue }));
};


const handleDropdownChange = (e, key) => {
  const value = e.target.value;
  if (value === 'Other') {
      setOtherSelected(prev => ({ ...prev, [key]: true }));
      setConfig(prevConfig => ({ ...prevConfig, [key]: '' }));
  } else {
      setOtherSelected(prev => ({ ...prev, [key]: false }));
      setConfig(prevConfig => ({ ...prevConfig, [key]: value }));
  }
};
 
  const renderConfigurationRow = (configuration, isDefaultConfig) => {
    const nonEditableFields = ['signalRequestId', 'id']; // Specify non-editable fields

    return (
      <tr>
    {configuration && Object.entries(configuration).map(([key, value]) => (
        <td key={key}>
            {isEditing && !isDefaultConfig && !nonEditableFields.includes(key) ? (
                dropdownFields[key] ? (
                    <div>
                        <select value={otherSelected[key] ? 'Other' : value} onChange={(e) => handleDropdownChange(e, key)}>
                            {dropdownFields[key].map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        {otherSelected[key] && (
                            <input type="text" value={value} onChange={(e) => handleChange(e, key)} />
                        )}
                    </div>
                ) : (
                    <input type="text" value={value} onChange={(e) => handleChange(e, key)} />
                )
            ) : (
                value
            )}
        </td>
    ))}
      </tr>
    );
  };

  
  const handleAddNew = () => {
    setIsEditing(true);
    setConfig({
      id: null,
      signalRequestId: signalRequestId,
      // Set other properties as needed
    });
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <>
         {loading && <LoadingIndicator text="Loading" />}
         {isSaving && <LoadingIndicator text="Saving" />}
    <br/>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        {!config && (
          <button style={unifiedButtonStyle} onClick={handleAddNew} >Override Default Config</button>
        )}
        {config && (
          <button onClick={handleEdit} disabled={isEditing} style={{ marginRight: '10px' }}>Modify Configuration</button>
        )}
        <button  style={unifiedButtonStyle} onClick={handleDefaultConfigurationClick}>
          {defaultConfig ? 'Hide System Default Config' : 'Show System Default Config'}
        </button>
      </div>
    
    <div className="mainContent">      
      {config && (
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              {Object.keys(config).map(key => <th key={key}>{key}</th>)}
            </tr>
          </thead>
          <tbody>
            {renderConfigurationRow(config)}
          </tbody>
        </table>
      )}

      {defaultConfig && (
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              {Object.keys(defaultConfig).map(key => <th key={key}>{key}</th>)}
            </tr>
          </thead>
          <tbody>
            {renderConfigurationRow(defaultConfig, true)}
          </tbody>
        </table>
      )}
    </div>
    <div>
        {isEditing && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button className="save-button" onClick={handleSave} disabled={isSaving}>Save</button>
            <button className="cancel-button" onClick={handleCancel}>Cancel</button>
            <button className="cancel-button" onClick={resetToDefaultConfig}>Reset To Default</button>
          </div>
        )}
        <br/>
      </div></>
  );
}

export default SpecialConfigurationDetails;
